import { Box, MultipleSelect } from '@color/continuum';
import { useEffect, useState } from 'react';

type Column = {
  id: string;
  name: string;
};

type ColumnVisibilityState = {
  columns: Column[];
  hiddenColumns: Set<string>;
  setHiddenColumns: (hiddenColumns: Set<string>) => void;
};

export const useColumnVisibility = (
  storageKey: string,
  columns: Column[]
): ColumnVisibilityState => {
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => {
    const storedHiddenColumns = localStorage.getItem(storageKey);
    return storedHiddenColumns ? new Set(JSON.parse(storedHiddenColumns)) : new Set<string>();
  });

  // nosemgrep:semgrep-rules.ui-useEffect
  useEffect(() => {
    const allIds = new Set(columns.map(({ id }) => id));
    // We intersect hidden with all column ids to do cleanup in case the columns have changed (added/removed)
    localStorage.setItem(storageKey, JSON.stringify([...allIds.intersection(hiddenColumns)]));
  }, [columns, hiddenColumns, storageKey]);

  return {
    columns,
    hiddenColumns,
    setHiddenColumns,
  };
};

export const ColumnVisibilityControl = (props: {
  columnVisibilityState: ColumnVisibilityState;
}) => {
  const { columns, hiddenColumns, setHiddenColumns } = props.columnVisibilityState;
  const allIds = new Set(columns.map(({ id }) => id));

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1000,
        backgroundColor: (theme) => theme.palette.background.paper,
        padding: 2,
        borderRadius: 1,
        boxShadow: 3,
        display: 'flex',
        gap: 2,
      }}
    >
      <MultipleSelect
        sx={{ maxWidth: '200px' }}
        label="Columns visible"
        optionalIndicatorIsEnabled={false}
        options={columns.map(({ id, name }) => ({ key: id, value: name }))}
        helperText={`${hiddenColumns.size} hidden`}
        selected={allIds.difference(hiddenColumns)}
        setSelected={(selected) => setHiddenColumns(allIds.difference(selected))}
      />
    </Box>
  );
};
